import React from 'react'
import Helmet from "next/head";
import ImageFactory from '../../adapters/cloudinary/ImageFactory';
import {validURL} from '../../adapters/helpers/Url';
import {getCanonicalURL} from '../../adapters/helpers/Utils';
import { OrganizationConstants, WebsiteConstants, Constants } from '../../adapters/helpers/Constants';

import PropTypes from 'prop-types'

import Head from 'next/head';

export default function Meta(props) {
    const { metadata, pagePath } = props;

    const title = metadata?.metaTitle;
    const description = metadata?.metaDescription;
    const keywords = metadata?.metaKeywords;
    const pagetype = metadata?.pageType;
    const noindex = metadata?.noIndex;
    const nofollow = metadata?.noFollow;
    const schemaObject = metadata?.schemaObjects;
    const isOrganizationSchemaVisible = metadata?.isOrganizationSchemaVisible;
    const isWebsiteSchemaVisible = metadata?.isWebsiteSchemaVisible;

    const openGraphPageTitle = metadata?.openGraphPageTitle;
    const openGraphDescription = metadata?.openGraphDescription;

    const socialAccounts = metadata?.socialsAccounts?.fields;

    let metaRobots = [];
    metaRobots.push(noindex ? 'noindex' : 'index');
    metaRobots.push(nofollow ? 'nofollow' : 'follow');

    let openGraphImageUrl = null;
    let logoUrl= null;

    if (metadata && metadata.openGraphImage && metadata.openGraphImage.fields) {
        openGraphImageUrl = ImageFactory.imageContentfullSrc(metadata?.openGraphImage?.fields?.asset?.fields?.file?.url);
    }
    
    if (metadata && metadata.logo && metadata.logo.fields) {
        logoUrl = ImageFactory.imageContentfullSrc(metadata?.logo?.fields?.asset?.fields?.file?.url);

    }
    let canonical = generateCanonical(metadata, pagePath);

    const createSchemaScripts = () => {
        if (!schemaObject || schemaObject.length === 0) {
          return null;
        }
  
        return schemaObject.map((oneSchema) => {
          return (
            <script
              defer
              type={Constants.typeLD}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  JSON.stringify(oneSchema)
                ),
              }}
            />
          );
        });
      };

    return (
        <>
            <Head>
                <meta name='viewport' content='width=device-width, initial-scale=1, minimum-scale=1' />
                <title>{title}</title>
                <link rel='manifest' href='/static/manifest.webmanifest' crossOrigin='use-credentials'/>
                <meta charSet='UTF-8'/>
                <meta name='description' content={description}/>
                <meta name='robots' content={metaRobots}/>
                <meta name='theme-color' content='#0057B8'/>
                <meta name='pagetype' content={pagetype}/>
                {keywords &&
                    <meta name='keywords' content={keywords}/>}

                {openGraphPageTitle &&
                    <meta property='og:title' content={openGraphPageTitle}/>}

                {openGraphDescription &&
                    <meta property='og:description' content={openGraphDescription}/>}

                {openGraphImageUrl &&
                    <meta property='og:image' content={openGraphImageUrl}/>}

                {canonical &&
                    <meta property='og:url' content={canonical}/>}

                {canonical &&
                    <link rel='canonical' href={canonical} />}
                {createSchemaScripts()}
            </Head>

            { isOrganizationSchemaVisible && <Helmet>
                <script
                        type={Constants.typeLD}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(`{
                                "@context": "${Constants.context}",
                                "@type": "${Constants.organization}",
                                "url": "${OrganizationConstants.url}",
                                "id": "${OrganizationConstants.id}",
                                "name": "${OrganizationConstants.brand}",
                                "description": "${OrganizationConstants.description}",
                                "parentOrganization": {
                                        "@type": "${Constants.organization}"
                                        "name": "${OrganizationConstants.parentOrganization}",
                                        "url": "${OrganizationConstants.parentOrganizationUrl}",
                                }
                                "sameAs": [
                                    "${OrganizationConstants.sameAsFacebook}",
                                    "${OrganizationConstants.sameAsYoutube}",
                                    "${OrganizationConstants.sameAsX}",
                                    ]                                    
                            }`),
                        }}
                      />
            </Helmet>
            }

            { isWebsiteSchemaVisible && <Helmet>
                <script
                        type={Constants.typeLD}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(`{
                                "@context": "${Constants.context}",
                                "@type": "${Constants.website}",                                
                                "url": "${WebsiteConstants.url}",
                                "potentialAction": {
                                    "@type": "${WebsiteConstants.type}",
                                    "target": "${WebsiteConstants.target}",
                                    "query-input": "${WebsiteConstants.queryInput}"
                                }
                            }`),
                        }}
                      />
            </Helmet>
            }
            
        </>
    );
}

Meta.propTypes = {
    metadata: PropTypes.object,
    pagePath: PropTypes.string,
};

function generateCanonical(metadata, pagePath) {
    let canonical = metadata?.canonicalUrl;

    if (canonical == null) {
        canonical = pagePath;
    }

    return validURL(canonical) ? canonical : getCanonicalURL(canonical);
}
